import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { snapshotsApi } from '@/lib/api';
import {
  formatAbsoluteTime,
  formatCompactDateTime,
  formatRelativeTime,
} from '@/lib/format-relative-time';
import { queryKeys } from '@/lib/query-keys';
import type { Snapshot, SnapshotKind, SnapshotsListResponse } from '@hillchart/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CameraIcon, HistoryIcon } from 'lucide-react';
import { type FormEvent, useRef, useState } from 'react';

interface SnapshotPanelProps {
  /** Hill whose snapshot history we're rendering. */
  hillId: string;
  /**
   * Currently-viewed snapshot id, or `null` when the live hill is shown.
   * Kept in the parent route so the main view and this panel stay in sync.
   */
  selectedId: string | null;
  /**
   * Selection handler. Passing `null` returns the view to the live hill.
   * Called synchronously from the click - no debounce; selection is local
   * state, not a network op.
   */
  onSelect: (id: string | null) => void;
  /**
   * When `true`, the panel renders for an archived hill:
   *  - The "Take snapshot" button is hidden (backend rejects with 409).
   *  - The "Live" pseudo-item disappears from the timeline - there is no
   *    live mode on a Done hill, only past states.
   * The history list itself stays interactive: users still scrub through
   * snapshots, and the ViewModeBanner's "Back to live" button continues
   * to bring the chart back to the canonical (final) state.
   */
  readOnly?: boolean;
}

/**
 * Right-hand drawer listing this hill's snapshot history.
 *
 * UX shape:
 * - On a live hill, a "Live" pseudo-item sits at the top of the timeline
 *   so users can return to the authoritative chart from any past
 *   snapshot. On a Done hill (`readOnly`) it disappears - the hill is
 *   frozen and the canonical view IS the final snapshot.
 * - Below, snapshots are rendered newest-first (the backend already orders
 *   them that way). Each shows a relative time, an absolute tooltip, the
 *   scope count, and the note if the user captured one. Snapshots whose
 *   `kind === 'final'` (taken automatically at hill completion) carry a
 *   "Final state" badge so they stand out in the timeline.
 * - "Take snapshot" opens a dialog with an optional 500-char note. Manual
 *   captures are a deliberate "freeze this moment" act - the dialog gives
 *   the user a place to write what the snapshot represents (release cut,
 *   demo state, weekly checkpoint) without forcing it.
 *
 * Data freshness:
 * - We don't subscribe to WebSocket snapshot events here. The user takes a
 *   snapshot, the mutation invalidates the list query, TanStack Query
 *   refetches - that's the round-trip. A scheduled daily snapshot landing
 *   on a connected tab will surface on the next refetch (tab focus / query
 *   staleness / manual refresh).
 */
export function SnapshotPanel({
  hillId,
  selectedId,
  onSelect,
  readOnly = false,
}: SnapshotPanelProps) {
  // The panel itself no longer mutates the cache directly - the capture
  // flow moved into `TakeSnapshotDialog` below which gets its own
  // QueryClient handle.
  const snapshotsQuery = useQuery({
    queryKey: queryKeys.snapshots(hillId),
    queryFn: () => snapshotsApi.list(hillId),
  });

  // Unwrap the envelope once so the JSX below reads against a plain array.
  // Filter out "no-op" duplicates - snapshots whose dots are identical to
  // the next older snapshot's. Going forward the daily scheduler skips
  // these at write time, but historical pollution from before the fix
  // (and any genuine "Take snapshot" the user fired on an unchanged hill)
  // would still clutter the timeline without this client-side filter.
  // `kind='final'` is always kept - it marks the irreversible completion
  // moment, never elided even if the dots happen to match the previous.
  const snapshots: Snapshot[] | undefined = snapshotsQuery.data?.snapshots
    ? filterInformativeSnapshots(snapshotsQuery.data.snapshots)
    : undefined;

  return (
    <aside
      aria-label="Snapshot history"
      className="flex h-full flex-col rounded-lg border border-slate-200 bg-white"
    >
      <header className="flex items-center justify-between gap-2 border-slate-200 border-b px-4 py-3">
        <div className="flex items-center gap-2">
          <HistoryIcon className="h-4 w-4 text-slate-500" />
          <h2 className="font-semibold text-brand-900 text-sm">History</h2>
        </div>
        {/*
          Manual snapshot capture is hidden in read-only mode. A done hill
          already carries a `kind='final'` snapshot taken in the same
          transaction as the completion - that's the canonical end state,
          and the backend would reject any further capture with 409.
        */}
        {!readOnly ? <TakeSnapshotDialog hillId={hillId} /> : null}
      </header>

      <div className="flex-1 overflow-y-auto">
        <ul className="flex flex-col" data-testid="snapshot-list">
          {/*
            "Live" pseudo-item. Rendered as a real <button> inside the list
            so screen readers walk it with arrow keys alongside the
            snapshots. `aria-current="true"` signals "this is the view
            you're on" when selected. Hidden on archived hills - there is
            no live mode for a Done hill.
          */}
          {!readOnly ? (
            <li>
              <SnapshotItem
                label="Live"
                sublabel="Current hill"
                isSelected={selectedId === null}
                onClick={() => onSelect(null)}
                accent="live"
                testId="snapshot-item-live"
              />
            </li>
          ) : null}

          {snapshotsQuery.isLoading && (
            <li className="px-4 py-3 text-slate-400 text-xs">Loading snapshots…</li>
          )}

          {snapshotsQuery.isError && (
            <li className="px-4 py-3 text-red-600 text-xs">
              Couldn't load snapshots: {snapshotsQuery.error.message}
            </li>
          )}

          {snapshots?.length === 0 && !snapshotsQuery.isLoading && (
            <li className="px-4 py-6 text-center text-slate-400 text-xs">
              No snapshots yet. Capture one to freeze this state.
            </li>
          )}

          {snapshots?.map((snap) => (
            <li key={snap.id}>
              <SnapshotItem
                label={formatRelativeTime(snap.capturedAt)}
                dateLabel={formatCompactDateTime(snap.capturedAt)}
                absoluteLabel={formatAbsoluteTime(snap.capturedAt)}
                sublabel={buildSnapshotSubline(snap)}
                note={snap.note}
                kind={snap.kind}
                isSelected={selectedId === snap.id}
                onClick={() => onSelect(snap.id)}
                accent="snapshot"
                testId={`snapshot-item-${snap.id}`}
              />
            </li>
          ))}
        </ul>
      </div>
    </aside>
  );
}

interface TakeSnapshotDialogProps {
  hillId: string;
}

/**
 * Dialog launched from the "Take snapshot" button. Captures an optional
 * note alongside the snapshot - the backend stores it in the snapshot row
 * so it surfaces in the timeline next to the date.
 *
 * The note is capped at 500 characters (matching `createSnapshotBodySchema`
 * in `@hillchart/shared`). Empty input passes `undefined` (not `""`) to
 * keep the wire payload minimal and so the row's `note` column lands as
 * NULL rather than an empty string.
 */
function TakeSnapshotDialog({ hillId }: TakeSnapshotDialogProps) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const submittingRef = useRef(false);

  const createSnapshot = useMutation({
    mutationFn: (body: { note?: string } | undefined) => snapshotsApi.create(hillId, body),
    onSuccess: (snapshot) => {
      // Prepend the new snapshot to the cached list rather than refetching
      // - the server echoes the full row, so we can land it
      // optimistically-enough without a round-trip.
      //
      // Idempotency: React 19 Strict Mode double-invokes `onSuccess` in
      // development. We dedupe by id so the second pass is a no-op -
      // without this, the list shows two entries with the same key until
      // the next refetch reconciles them.
      qc.setQueryData<SnapshotsListResponse>(queryKeys.snapshots(hillId), (prev) => {
        if (!prev) return { snapshots: [snapshot] };
        if (prev.snapshots.some((s) => s.id === snapshot.id)) return prev;
        return { snapshots: [snapshot, ...prev.snapshots] };
      });
      // Reset the dialog state so the next open starts fresh.
      setNote('');
      setOpen(false);
    },
    onSettled: () => {
      submittingRef.current = false;
    },
  });

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (submittingRef.current) return;
    submittingRef.current = true;
    const trimmed = note.trim();
    createSnapshot.mutate(trimmed.length > 0 ? { note: trimmed } : undefined);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Block dismissal while a request is in flight so the user can't
        // close the dialog mid-capture and miss the success/error state.
        if (createSnapshot.isPending) return;
        if (!next) setNote('');
        setOpen(next);
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="take-snapshot">
          <CameraIcon className="h-3.5 w-3.5" />
          Take snapshot
        </Button>
      </DialogTrigger>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Capture a snapshot</DialogTitle>
            <DialogDescription>
              Freeze the current hill state. Add an optional note to remember what this snapshot
              represents — release cut, demo state, weekly checkpoint, …
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4 space-y-2">
            <label htmlFor="snapshot-note" className="font-medium text-slate-700 text-sm">
              Note <span className="font-normal text-slate-400">(optional)</span>
            </label>
            <textarea
              id="snapshot-note"
              data-testid="snapshot-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={500}
              rows={3}
              placeholder="e.g. End of week 2 — demo to Patrick"
              disabled={createSnapshot.isPending}
              className="w-full resize-none rounded-md border border-slate-200 px-3 py-2 text-sm focus-visible:border-brand-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-200 disabled:cursor-not-allowed disabled:opacity-50"
            />
            <p className="text-right text-[11px] text-slate-400 tabular-nums">
              {note.length} / 500
            </p>
          </div>

          {createSnapshot.isError ? (
            <p className="text-red-600 text-sm" role="alert">
              Snapshot failed: {createSnapshot.error.message}
            </p>
          ) : null}

          <DialogFooter className="mt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setNote('');
                setOpen(false);
              }}
              disabled={createSnapshot.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={createSnapshot.isPending}
              data-testid="snapshot-confirm"
            >
              {createSnapshot.isPending ? 'Capturing…' : 'Capture'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Drop snapshots whose dots are identical to the next older snapshot in
 * the list. The backend list is ordered `desc(capturedAt)` (newest
 * first), so "next older" is the snapshot at `i + 1`. Oldest snapshot
 * (last entry) is always kept - no older neighbour to compare against.
 *
 * `kind='final'` is exempt and always kept. It marks the irreversible
 * end-of-life moment; even if the user moved no dots between the
 * previous capture and completion, the final snapshot is the canonical
 * archive marker and must remain visible.
 *
 * Exported for testing.
 */
export function filterInformativeSnapshots(items: Snapshot[]): Snapshot[] {
  const kept: Snapshot[] = [];
  for (let i = 0; i < items.length; i++) {
    const current = items[i];
    if (!current) continue;
    if (current.kind === 'final') {
      kept.push(current);
      continue;
    }
    const older = items[i + 1];
    if (!older) {
      // Oldest entry — first time this state was recorded, always
      // informative.
      kept.push(current);
      continue;
    }
    if (!dotsLooksTheSame(current.dots, older.dots)) {
      kept.push(current);
    }
  }
  return kept;
}

/**
 * Structural equality on two snapshot-dot arrays. Mirrors the backend's
 * `dotsEqualForSnapshot` so the client filter matches what the scheduler
 * would do going forward - keep the two in sync if the comparison
 * criteria evolve.
 *
 * Order-independent: we key both sides by milestone id, so a resync that
 * reshuffles dot insertion order on either side does not register as a
 * change.
 */
function dotsLooksTheSame(a: Snapshot['dots'], b: Snapshot['dots']): boolean {
  if (a.length !== b.length) return false;
  const byId = new Map<string, Snapshot['dots'][number]>(a.map((d) => [d.externalScopeId, d]));
  for (const incoming of b) {
    const existing = byId.get(incoming.externalScopeId);
    if (!existing) return false;
    if (
      existing.x !== incoming.x ||
      existing.color !== incoming.color ||
      existing.completed !== incoming.completed ||
      existing.externalScopeName !== incoming.externalScopeName ||
      existing.displayOrder !== incoming.displayOrder
    ) {
      return false;
    }
  }
  return true;
}

/**
 * Compose the one-liner below the snapshot's date.
 *
 * Lives outside the component so it's trivially testable and so we don't
 * keep a prop permutation (dot count + completion count + ...) in the
 * SnapshotItem signature.
 */
function buildSnapshotSubline(snap: Snapshot): string {
  const total = snap.dots.length;
  const done = snap.dots.filter((d) => d.completed).length;
  if (total === 0) return 'Empty';
  if (done === 0) return `${total} ${total === 1 ? 'scope' : 'scopes'}`;
  return `${total} ${total === 1 ? 'scope' : 'scopes'} · ${done} done`;
}

interface SnapshotItemProps {
  label: string;
  /**
   * Visible, compact absolute date+time (e.g. `Apr 21, 14:33`). Rendered
   * to the right of the main label so users can read the exact moment at
   * a glance - "2 hours ago" alone is ambiguous once the history grows.
   * Omitted for the Live pseudo-item (no capture timestamp to show).
   */
  dateLabel?: string;
  absoluteLabel?: string;
  sublabel: string;
  note?: string | null;
  /**
   * Snapshot kind. `'final'` carries a "Final state" pill so the entry
   * stands out in the timeline of an archived hill. `'regular'` is the
   * default appearance. Omitted on the synthetic "Live" item.
   */
  kind?: SnapshotKind;
  isSelected: boolean;
  onClick: () => void;
  /**
   * Visual accent. `live` keeps the item at the top visually anchored with
   * an emerald dot (mirrors the WS "Live" badge in the page header), while
   * `snapshot` uses a neutral slate marker.
   */
  accent: 'live' | 'snapshot';
  testId: string;
}

function SnapshotItem({
  label,
  dateLabel,
  absoluteLabel,
  sublabel,
  note,
  kind,
  isSelected,
  onClick,
  accent,
  testId,
}: SnapshotItemProps) {
  const isFinal = kind === 'final';
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={isSelected ? 'true' : undefined}
      data-testid={testId}
      title={absoluteLabel}
      className={`w-full cursor-pointer border-slate-100 border-b px-4 py-3 text-left transition-colors last:border-b-0 ${
        isSelected ? 'bg-brand-50' : 'hover:bg-slate-50'
      }`}
    >
      <div className="flex items-start gap-2">
        <span
          aria-hidden="true"
          className={`mt-1.5 inline-block h-2 w-2 shrink-0 rounded-full ${
            accent === 'live' ? 'bg-emerald-500' : isFinal ? 'bg-amber-500' : 'bg-slate-400'
          }`}
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-2">
            <span className="flex min-w-0 items-center gap-1.5">
              <span className="truncate font-medium text-slate-900 text-sm">{label}</span>
              {isFinal ? (
                <span
                  className="shrink-0 rounded bg-amber-100 px-1.5 py-0.5 font-medium text-[10px] text-amber-900 uppercase tracking-wide"
                  data-testid="final-state-badge"
                >
                  Final state
                </span>
              ) : null}
            </span>
            {dateLabel !== undefined && (
              <span className="shrink-0 text-[11px] text-slate-400 tabular-nums">{dateLabel}</span>
            )}
          </div>
          <p className="mt-0.5 truncate text-slate-500 text-xs">{sublabel}</p>
          {note !== undefined && note !== null && note.length > 0 && (
            <p className="mt-1 line-clamp-2 text-slate-600 text-xs italic">{note}</p>
          )}
        </div>
      </div>
    </button>
  );
}
